import { useEffect, useMemo, useRef, useState } from "react";
import { colors, css, fonts, frames, glyphs } from "../../../../Styles";
import { AicoConnection, LoadingSpinner, NewButton, NewButtonList, NewInputList, NewSelect, PageHeader, Setting, UpdateServerModal } from "../../../../Components";
import { useAicoConnection } from "../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { ButtonTypes, NewFieldStyles, NewInputListHandles, SelectFieldOption_Values, systemTypeOptions } from "../../../../Definitions";
import { Graph_QL, useSystem, useUser } from "../../../../Services";
import { selectAllSystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/systemLimitationsSlice";
import { EXTENDED_COMPANIES, ExtendedCompaniesArgs, ExtendedCompaniesResponse } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/extendedCompanies";
import { SystemLimitations } from "../../../../Services/Agrisys_GqlDataCaller/SystemCalls/GQLCalls/systemLimitations";
import { useAppSelector } from "../../../../Store";
import { useLazyQuery } from "@apollo/client";
import { VersionsResponse, VersionsArgs, VERSIONS } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/versions";
import { LimitOffsetPaginationMeta } from "../../../../Services/Agrisys_GqlDataCaller/sharedTypes";

// TODO
// import { systemTypeOptions } from "../../../../Navigation/Menus/systemMenu";


export default function Company() {
	// #region | GraphQL
	const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
	const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
	const [domain, setDomainState] = useState<string | null>(null);
	const { userFarms } = useUser();
	// #endregion

	function handleDomainChange(value: SelectFieldOption_Values | undefined) {
		setDomainState(value?.value ?? null);
	}

	// #region | State Limitation
	const { getSystemLimitationsInfo, maintainSystemLimitations } = useSystem(connection)
	const stateLimits = useAppSelector(selectAllSystemLimitations);
	const [localLimits, setLocalLimits] = useState<SystemLimitations[] | undefined>(undefined);
	const limitInputRef = useRef<Record<number, NewInputListHandles>>({});

	useEffect(() => {
		if (domain)
			getSystemLimitationsInfo(domain);
	}, [getSystemLimitationsInfo, domain]);

	// Set default values for input fields
	useEffect(() => {
		if (!localLimits) {
			Object.values(limitInputRef.current).forEach((ref) => { ref.refs.current.map((valueHandle) => valueHandle.setValue(0)) })
			return
		};

		localLimits.forEach((l) => {
			const ref = limitInputRef.current[l.fk_system_types];
			if (!ref) return;

			ref.refs.current[0].setValue(l.systems_limit);
			ref.refs.current[1].setValue(l.stations_limit);
		});
	}, [localLimits]);

	useEffect(() => {
		setLocalLimits(stateLimits);
	}, [stateLimits]);

	// Save limits
	const UpdateStateLimits = (systemId: number) => {
		if (!stateLimits) return;

		const ref = limitInputRef.current[systemId];
		if (!ref) return;

		const config = {
			systems_limit: Number(ref.refs.current[0].value) ?? undefined,
			stations_limit: Number(ref.refs.current[1].value) ?? undefined,
		};

		if (!domain) return;

		maintainSystemLimitations(
			domain,
			systemId,
			config.systems_limit,
			config.stations_limit
		);

		setLocalLimits(prev =>
			prev?.map(item => item.fk_system_types === systemId ? {
				...item, systems_limit: config.systems_limit ?? 0, stations_limit: config.stations_limit ?? 0
			} : item)
		);
	};

	// System Input Fields
	const SystemInputs = systemTypeOptions.map((system, index) => {
		const anyDirty = localLimits?.some(local => {
			const backend = stateLimits?.find(x => x.fk_system_types === local.fk_system_types);
			return backend && (
				local.systems_limit !== backend.systems_limit ||
				local.stations_limit !== backend.stations_limit
			);
		}) ?? false;

		const id = system.id;

		const local = localLimits?.find(x => x.fk_system_types === id);
		const backend = stateLimits?.find(x => x.fk_system_types === id);

		const backendSystems = backend?.systems_limit ?? undefined;
		const backendStations = backend?.stations_limit ?? undefined;

		const hasChanged = local?.systems_limit !== backendSystems || local?.stations_limit !== backendStations;

		const disabled = anyDirty && !hasChanged;

		return (
			<div key={id} style={{ display: "grid", gridTemplateColumns: "200px 280px 1fr", gap: "10px" }}>
				<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					{system.label}
				</div>

				<NewInputList
					key={domain}
					ref={(el) => { if (el) limitInputRef.current[system.id] = el }}
					placeholders={["", ""]}
					isDisabled={[disabled, disabled]}
					headers={index === 0 ? ["systems", "locations"] : undefined}
					valueOverrides={{
						valueTypes: ["number", "number"],
						defaultValues: [0, 0]
					}}
					fieldOverrides={{
						fieldStyle: NewFieldStyles.greyTertiary,
						fieldWidth: "137.5px"
					}}
					onChanges={[
						(systemsValue: string) => {
							setLocalLimits(prev => {
								if (prev === undefined) return prev;
								if (!prev.find((item) => item.fk_system_types === id)) {
									if (domain == null) {
										return prev
									}
									return [...prev, { fk_system_types: id, stations_limit: 0 || 0, domain: domain!, systems_limit: Number(systemsValue) }]
								}
								return prev.map(item => item.fk_system_types === id ? { ...item, systems_limit: Number(systemsValue) || 0 } : item);
							});
						},
						(stationsValue: string) => {
							setLocalLimits(prev => {
								if (prev === undefined) return prev;
								if (!prev.find((item) => item.fk_system_types === id)) {
									if (domain == null) {
										return prev
									}
									return [...prev, { fk_system_types: id, stations_limit: Number(stationsValue) || 0, domain: domain!, systems_limit: 0 }]
								}
								return prev.map(item => item.fk_system_types === id ? { ...item, stations_limit: Number(stationsValue) || 0 } : item);
							});
						}]}
				/>

				{hasChanged && (<div style={{ display: "flex", alignItems: "center", paddingTop: index === 0 ? "14px" : undefined, ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkSecondary }}>
					<NewButtonList
						contents={["", ""]}
						buttonType={ButtonTypes.arrowButton}
						startEnhancers={[glyphs.Check({}), glyphs.Close({})]}
						onClick={() => UpdateStateLimits(id)}
					/>
				</div>)}
			</div>
		);
	})
	// #endregion

	// #region | Update Company Version
	const limit = 50;
	const [isUpdateServerModalOpen, setIsUpdateServerModalOpen] = useState<boolean>(false);

	const [selectedVersion, setSelectedVersion] = useState<SelectFieldOption_Values | undefined>(undefined);
	const [pagination, setPagination] = useState<LimitOffsetPaginationMeta | undefined>(undefined)

	const [fetchCompany, { data: companyFetchData, loading: companyFetchLoading, error: companyFetchError }] = useLazyQuery<ExtendedCompaniesResponse, ExtendedCompaniesArgs>(EXTENDED_COMPANIES, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	const [fetchVersions, { data: versionsFetchData, loading: versionsFetchLoading, error: versionsFetchError }] = useLazyQuery<VersionsResponse, VersionsArgs>(VERSIONS, {
		fetchPolicy: "no-cache",
		context: {
			headers: {
				tocloud: true
			}
		}
	});

	const companyData = companyFetchData?.user.extendedCompanies?.[0];

	const companyId = useMemo(() => {

		setSelectedVersion(undefined);

		if (!domain || !userFarms) {
			return null;
		}
		return userFarms.find(e => e.domain === domain)?.company_id ?? null;

	}, [domain, userFarms]);

	const fetchCompanyByDomain = () => {
		if (!companyId) return;

		fetchCompany({
			variables: { input: { ids: [companyId] } }
		});
	};

	useEffect(fetchCompanyByDomain, [companyId]);

	useEffect(() => {
		fetchVersions({
			variables: {
				pagination: {
					limit: limit,
					offset: 0,
				},
				sort: 'DESC',
			}
		});
	}, []);

	const currentVersion = companyData?.version;

	const availableVersions: SelectFieldOption_Values[] = useMemo(() => {
		const versionFetch = versionsFetchData?.server.versionsPaginated
		if (versionFetch) {
			setPagination(versionFetch.meta);
		}

		let versions = versionFetch?.data;

		if (currentVersion) {
			versions = versions?.filter(e => e.version_id > currentVersion.version_id) ?? [];
		}

		return versions?.map(e => ({ label: e.version, id: e.version_id })) ?? [];
	}, [versionsFetchData, currentVersion]);

	// #endregion

	return (
		<frames.Page_inner>
			<PageHeader
				topHeader='Companies'
				subSelect={{
					type: "element",
					// element: < AicoConnection hideLANControllers/>
					element:
						<NewSelect
							placeholder="Domain"
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greyDarkPrimary,
								fieldWidth: "230px"
							}}
							options={userFarms?.map(farm => ({ label: farm.domain, value: farm.domain })) ?? []}
							isClearable
							isSearchable
							onChange={handleDomainChange}
							valueOverrides={{
								defaultValue: domain ? { label: domain, value: domain, id: domain } : undefined
							}}
						/>
				}}
			/>

			<frames._page_inner_content_scroll>
				{domain ? (<div style={{ display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
					<Setting
						startEnhancer={glyphs.FarmInfo({ size: "25px" })}
						header={"Number of systems and locations"}
						content={<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}> {SystemInputs} </div>}
					/>

					<Setting
						startEnhancer={glyphs.FarmInfo({ size: "25px" })}
						header={"Company version"}
						content={<>{!currentVersion ? <>Company has no version, please contact an admin to update</> :
							<div style={{ display: "flex", flexDirection: "column", gap: "10px", ...fonts.fontSize3, ...fonts.paragraph }}>
								<NewButtonList
									contents={["Fetch", "Update Version"]}
									overrides={{
										baseStyles: { width: "fit-content", backgroundColor: colors.greyTertiary, height: "30px", padding: "0 10px" },
										contentStyles: { color: colors.greyDarkPrimary, textShadow: "none", ...fonts.fontSize4 },
										hoverStyles: { backgroundColor: colors.greyLightPrimary, color: colors.greyDarkPrimary }
									}}
									onClick={(i) => {
										if (i === 0) { fetchCompanyByDomain() };
										if (i === 1) { setIsUpdateServerModalOpen(true) };
									}}
									disabled={[false, !!versionsFetchError || versionsFetchLoading || !selectedVersion || !(domain && typeof selectedVersion?.id === "number")]}
								/>
								{companyFetchLoading && <LoadingSpinner />}
								{companyFetchError && <p style={{ ...fonts.fontSize3, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>Error: failed to fetch current version</p>}
								<div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}> current version: {currentVersion?.version ?? "unknown"} </div>
								<div style={{ display: "flex", flexDirection: "row", gap: "10px", ...fonts.fontSize3, ...fonts.paragraph }}>
									<NewSelect
										options={availableVersions}
										onChange={e => setSelectedVersion(e)}
										valueOverrides={{ defaultValue: selectedVersion }}
										isRequired
										isDisabled={!!versionsFetchError || versionsFetchLoading}
										fieldOverrides={{
											fieldStyle: NewFieldStyles.greyTertiary,
										}}
										isSearchable
									/>
									<NewButton
										content={""}
										buttonType={ButtonTypes.glyphButton}
										startEnhancer={glyphs.Fetch({ fillColor: colors.blackPrimary })}
										onClick={() =>
											fetchVersions({
												variables: {
													pagination: {
														limit: limit,
														offset: 0,
													},
													sort: 'DESC',
												}
											})
										}
										disabled={versionsFetchLoading}
									/>
								</div>
								{versionsFetchLoading && <LoadingSpinner />}
								{versionsFetchError && <p style={{ ...fonts.fontSize3, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px" }}>Error: failed to versions</p>}
								{!versionsFetchLoading && pagination && pagination.hasNextPage && (
									<NewButton
										content={"Fetch more"}
										onClick={() =>
											fetchVersions({
												variables: {
													pagination: {
														limit: limit,
														offset: pagination.offset + limit,
													},
													sort: 'DESC',
												}
											})
										}
										disabled={versionsFetchLoading}
									/>
								)}
							</div>
						}
						</>}
					/>
				</div>) : (<p style={{ ...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center" }}>Please select a domain</p>)}
			</frames._page_inner_content_scroll>

			{domain && typeof selectedVersion?.id === "number" && isUpdateServerModalOpen &&
				<UpdateServerModal
					closeModal={() => {
						fetchCompanyByDomain();
						setIsUpdateServerModalOpen(false);
					}}
					domain={domain}
					version={selectedVersion.label}
				/>
			}
		</frames.Page_inner>
	)
}